import { useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { motion } from "framer-motion"
import {
  ArrowLeft,
  Bell,
  ChevronRight,
  BellOff,
  BellRing,
  Dumbbell,
  Heart,
  BarChart3,
  Clock,
  CheckCheck,
  Flame,
  Weight,
} from "lucide-react"
import { useNotifications } from "@/hooks/useNotifications"
import { usePushSubscription } from "@/hooks/usePushSubscription"
import type { NotificationRow, NotificationType } from "@/lib/supabase"

const NOTIF_CONFIG: Record<NotificationType, { icon: typeof Bell; color: string; bg: string }> = {
  "post-session": { icon: Dumbbell,  color: "#06b6d4", bg: "rgba(6,182,212,0.10)" },
  hooper:         { icon: Heart,     color: "#22c55e", bg: "rgba(34,197,94,0.10)" },
  // Anciennes entrées « Ton bilan de la semaine » (plus écrites depuis le 16/10)
  weekly:         { icon: BarChart3, color: "#f97316", bg: "rgba(249,115,22,0.10)" },
  reminder:       { icon: Clock,     color: "#8b5cf6", bg: "rgba(139,92,246,0.10)" },
  achievement:    { icon: Flame,     color: "#eab308", bg: "rgba(234,179,8,0.10)" },
  weight:         { icon: Weight,    color: "#67e8f9", bg: "rgba(103,232,249,0.08)" },
  // « Tes notifications sont coupées » (send-push, quand le dernier appareil décroche)
  push:           { icon: BellOff,   color: "#f59e0b", bg: "rgba(245,158,11,0.10)" },
}

// Repli pour un type inconnu de cette version de l'app (écrit par une version plus
// récente du serveur) : une icône neutre plutôt qu'un écran qui plante.
const NOTIF_FALLBACK = { icon: Bell, color: "#94a3b8", bg: "rgba(148,163,184,0.10)" }

// ── Helpers de regroupement par fenêtre temporelle ──
type Group = { label: string; items: NotificationRow[] }

function startOfDay(d: Date): Date {
  const x = new Date(d)
  x.setHours(0, 0, 0, 0)
  return x
}

function groupNotifications(notifs: NotificationRow[]): Group[] {
  const today = startOfDay(new Date())
  const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1)
  const weekStart = new Date(today); weekStart.setDate(weekStart.getDate() - today.getDay() || -6)
  if (today.getDay() === 0) weekStart.setDate(today.getDate() - 6)
  else weekStart.setDate(today.getDate() - (today.getDay() - 1))
  const lastWeekStart = new Date(weekStart); lastWeekStart.setDate(lastWeekStart.getDate() - 7)

  const buckets: Record<string, NotificationRow[]> = {
    "Aujourd'hui": [],
    "Hier": [],
    "Cette semaine": [],
    "Semaine dernière": [],
    "Plus ancien": [],
  }

  for (const n of notifs) {
    const d = new Date(n.created_at)
    if (d >= today)              buckets["Aujourd'hui"].push(n)
    else if (d >= yesterday)     buckets["Hier"].push(n)
    else if (d >= weekStart)     buckets["Cette semaine"].push(n)
    else if (d >= lastWeekStart) buckets["Semaine dernière"].push(n)
    else                          buckets["Plus ancien"].push(n)
  }

  return Object.entries(buckets)
    .filter(([, items]) => items.length > 0)
    .map(([label, items]) => ({ label, items }))
}

function formatTime(iso: string, label: string): string {
  const d = new Date(iso)
  if (label === "Aujourd'hui" || label === "Hier") {
    return d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })
  }
  return d.toLocaleDateString("fr-FR", { weekday: "short" }) + " " +
         d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })
}

export const NotificationsPage = () => {
  const navigate = useNavigate()
  const { notifications, unreadCount, loading, markRead, markAllRead } = useNotifications()
  const { state: pushState } = usePushSubscription()

  const groups = useMemo(() => groupNotifications(notifications), [notifications])

  const handleNotifClick = async (notif: NotificationRow) => {
    if (!notif.read) await markRead(notif.id)
    if (notif.action_url) navigate(notif.action_url)
  }

  return (
    <div className="min-h-screen bg-[#060a13] pb-10 cursor-default relative">

      {/* HEADER */}
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="px-5 pt-5 pb-4 relative z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/dashboard")}
              className="w-9 h-9 rounded-lg bg-gray-800/50 flex items-center justify-center hover:bg-gray-700/50 transition-colors cursor-pointer">
              <ArrowLeft className="w-[18px] h-[18px] text-gray-400" />
            </button>
            <div>
              <h1 className="text-[20px] font-bold text-white tracking-tight">Notifications</h1>
              {unreadCount > 0 && (
                <p className="text-[12px] text-cyan-400 font-medium mt-0.5">
                  {unreadCount} non lue{unreadCount > 1 ? "s" : ""}
                </p>
              )}
            </div>
          </div>

          {unreadCount > 0 && (
            <button onClick={markAllRead}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg cursor-pointer transition-all active:scale-[0.97]"
              style={{ background: "rgba(6,182,212,0.08)", border: "1px solid rgba(6,182,212,0.18)" }}>
              <CheckCheck className="w-3.5 h-3.5 text-cyan-400" />
              <span className="text-[11px] font-semibold text-cyan-400">Tout lire</span>
            </button>
          )}
        </div>
      </motion.div>

      {/* ACCÈS AUX RÉGLAGES
          Les six réglages de notification ont déménagé dans Profil → Réglages : cet
          onglet redevient un fil pur. Ce raccourci reste ici pour ceux qui viennent les
          chercher par réflexe à l'ancien endroit. */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 }}
        className="px-4 mb-5 relative z-10"
      >
        <button
          onClick={() => navigate('/dashboard/profile/settings')}
          className="w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl text-left cursor-pointer transition-all active:scale-[0.99]"
          style={{ background: 'rgba(13,20,32,0.92)', border: '1px solid rgba(30,41,59,0.55)' }}
        >
          <span
            className="w-9 h-9 rounded-xl grid place-items-center shrink-0"
            style={
              pushState === 'denied'
                ? { background: 'rgba(100,116,139,0.12)', border: '1px solid rgba(100,116,139,0.2)', color: '#64748b' }
                : pushState === 'interrupted'
                  ? { background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.20)', color: '#f59e0b' }
                  : { background: 'rgba(6,182,212,0.10)', border: '1px solid rgba(6,182,212,0.20)', color: '#06b6d4' }
            }
          >
            {pushState === 'denied' || pushState === 'interrupted' ? <BellOff className="w-4 h-4" /> : <BellRing className="w-4 h-4" />}
          </span>
          <span className="flex-1 min-w-0">
            <span className="block text-[13.5px] font-semibold text-white">Réglages des notifications</span>
            <span className="block text-[11.5px] text-gray-500 mt-0.5">
              {pushState === 'denied'
                ? 'Notifications bloquées par ton navigateur'
                : pushState === 'interrupted'
                  ? 'Notifications interrompues · touche pour les réactiver'
                  : pushState === 'active'
                    ? 'Push actives · choisir lesquelles recevoir'
                    : 'Push non activées sur cet appareil'}
            </span>
          </span>
          <ChevronRight className="w-4 h-4 text-gray-600 shrink-0" />
        </button>
      </motion.div>

      {/* LOADING */}
      {loading && (
        <div className="px-4 mt-8 flex flex-col items-center gap-2 relative z-10">
          <div className="w-6 h-6 rounded-full border-2 border-gray-700 border-t-cyan-400 animate-spin" />
          <p className="text-[12px] text-gray-500">Chargement…</p>
        </div>
      )}

      {/* EMPTY STATE */}
      {!loading && notifications.length === 0 && (
        <div className="px-6 mt-8 flex flex-col items-center text-center relative z-10">
          <div className="w-16 h-16 rounded-2xl bg-gray-900/60 border border-gray-800/60 flex items-center justify-center mb-4">
            <Bell className="w-7 h-7 text-gray-600" />
          </div>
          <p className="text-[15px] font-semibold text-gray-300">Aucune notification</p>
          <p className="text-[12px] text-gray-500 mt-1 max-w-[280px]">
            Tu seras alerté ici pour tes séances, ton Hooper du matin et tes bilans hebdo.
          </p>
        </div>
      )}

      {/* GROUPS */}
      {!loading && groups.length > 0 && (
        <div className="px-4 relative z-10">
          {groups.map((group, gIdx) => (
            <motion.div key={group.label} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 * gIdx }} className="mb-5">
              <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider ml-1 mb-2">
                {group.label}
              </p>

              <div className="bg-gray-900/50 border border-gray-800/50 rounded-2xl overflow-hidden">
                {group.items.map((notif, nIdx) => {
                  const cfg = NOTIF_CONFIG[notif.type] ?? NOTIF_FALLBACK
                  const Icon = cfg.icon
                  return (
                    <button key={notif.id} onClick={() => handleNotifClick(notif)}
                      className="w-full flex items-start gap-3 px-4 py-3.5 text-left transition-all active:scale-[0.99]"
                      style={{
                        borderBottom: nIdx < group.items.length - 1 ? "1px solid rgba(30,41,59,0.25)" : "none",
                        cursor: notif.action_url ? "pointer" : "default",
                        background: !notif.read ? "rgba(6,182,212,0.03)" : "transparent",
                      }}>
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 mt-0.5"
                        style={{ background: cfg.bg, border: `1px solid ${cfg.color}20` }}>
                        <Icon className="w-[18px] h-[18px]" style={{ color: cfg.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-[13px] font-semibold truncate" style={{ color: !notif.read ? "#fff" : "#94a3b8" }}>
                            {notif.title}
                          </span>
                          <span className="text-[10px] text-gray-600 shrink-0">
                            {formatTime(notif.created_at, group.label)}
                          </span>
                        </div>
                        <p className="text-[12px] mt-0.5 leading-relaxed" style={{ color: !notif.read ? "rgba(255,255,255,0.65)" : "#64748b" }}>
                          {notif.body}
                        </p>
                      </div>
                      {!notif.read && (
                        <div className="w-[8px] h-[8px] rounded-full shrink-0 mt-2"
                          style={{ background: "#06b6d4", boxShadow: "0 0 8px rgba(6,182,212,0.5)" }} />
                      )}
                    </button>
                  )
                })}
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  )
}
